import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertDialog } from "@/components/ui/AlertDialog";
import { ChevronDownIcon, SignOutIcon, UserIcon } from "@/components/ui/icons";
import { signOut } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { mediaUrl } from "@/lib/media";

interface Props {
  name: string;
  email: string | null;
  role: string;
  /** Fallback when the account has no uploaded picture. */
  monogram: string;
  /** The account's picture, once `GET /me` has resolved one. */
  avatarUrl?: string | null;
}

/**
 * The name in the top bar, as a menu.
 *
 * Two items, and it should stay two: this is the corner people click when they
 * want to leave, and every extra row is another thing to read first. Anything
 * that is a setting belongs on the profile page the first item opens.
 *
 * Built by hand rather than pulled from a library. The behaviour a menu owes
 * you is small and well defined — close on outside click, close on Escape and
 * hand focus back, move with the arrow keys, announce itself as a menu — and
 * all of it is below. What a library adds at this size is a dependency and a
 * second set of class names to reconcile with the design tokens.
 */
export function AccountMenu({ name, email, role, monogram, avatarUrl }: Props) {
  const [open, setOpen] = useState(false);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  // Outside click and Escape, bound only while open — a document listener that
  // lives for the life of the app runs on every click in the panel forever.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      // Focus goes back to the trigger, not to the top of the document. A menu
      // that drops focus on close strands anyone using a keyboard.
      triggerRef.current?.focus();
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Focus the first item when the menu opens, so the keyboard route works
  // without a second Tab.
  useEffect(() => {
    if (open) firstItemRef.current?.focus();
  }, [open]);

  const moveFocus = (direction: 1 | -1) => {
    const items = containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
    if (!items || items.length === 0) return;

    const list = Array.from(items);
    const index = list.findIndex((item) => item === document.activeElement);
    list[(index + direction + list.length) % list.length]?.focus();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((shown) => !shown)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          "rounded-control flex items-center gap-2.5 py-1 pr-1.5 pl-1 transition-colors duration-150",
          "hover:bg-surface-sunken",
          open && "bg-surface-sunken",
        )}
      >
        {/* The uploaded picture when there is one, initials when there is not.
            An `<img>` that 404s would leave a torn-page glyph in the top bar of
            every screen, so the failure falls back rather than showing. */}
        {mediaUrl(avatarUrl) ? (
          <img
            src={mediaUrl(avatarUrl) ?? undefined}
            alt=""
            aria-hidden="true"
            decoding="async"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
            // Ring and fill as in `Avatar`, so a transparent PNG has an edge.
            className="bg-brand-soft ring-border size-9 shrink-0 rounded-full object-cover ring-1"
          />
        ) : (
          <span
            className="bg-brand text-brand-content flex size-9 shrink-0 items-center justify-center rounded-full text-[0.6875rem] font-semibold"
            aria-hidden="true"
          >
            {monogram}
          </span>
        )}

        {/* The name is dropped on narrow viewports, where the avatar alone is
            unambiguous and the bar has no room. The accessible name of the
            button does not change with it. */}
        <span className="hidden min-w-0 text-left leading-tight md:block">
          <span className="text-content block max-w-40 truncate text-[0.8125rem] font-semibold">
            {name}
          </span>
          <span className="text-content-subtle block max-w-40 truncate text-[0.6875rem]">
            {role}
          </span>
        </span>

        <ChevronDownIcon
          aria-hidden="true"
          className={cn(
            "text-content-subtle hidden size-4 shrink-0 transition-transform duration-200 md:block",
            open && "rotate-180",
          )}
        />
        <span className="sr-only">Account menu for {name}</span>
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Account"
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              moveFocus(1);
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              moveFocus(-1);
            }
          }}
          className={cn(
            "panel rounded-panel absolute right-0 z-50 mt-2 w-60 overflow-hidden p-1.5 shadow-lg",
            "animate-rise",
          )}
        >
          {/* Who this menu belongs to. The trigger hides the name below `md`,
              and a two-item menu with no context is easy to open on the wrong
              account in a browser signed into two. */}
          <div className="border-border mb-1.5 border-b px-2.5 pt-1.5 pb-2.5">
            <p className="text-content truncate text-[0.8125rem] font-semibold">{name}</p>
            {email ? (
              <p className="text-content-subtle truncate text-[0.6875rem]">{email}</p>
            ) : null}
          </div>

          <MenuItem
            ref={firstItemRef}
            icon={UserIcon}
            label="Profile"
            onSelect={() => {
              setOpen(false);
              void navigate("/settings/profile");
            }}
          />
          {/* Confirmed first: the item sits right under "Profile", and a
              mis-click otherwise drops whatever was half-edited on the page. */}
          <MenuItem
            icon={SignOutIcon}
            label="Sign out"
            danger
            onSelect={() => {
              setOpen(false);
              setConfirmingSignOut(true);
            }}
          />
        </div>
      ) : null}

      <AlertDialog
        open={confirmingSignOut}
        tone="danger"
        icon={SignOutIcon}
        title="Sign out?"
        message="You will need to sign in again to use the admin panel. Anything unsaved on this page will be lost."
        confirmLabel={signingOut ? "Signing out…" : "Yes, sign out"}
        busy={signingOut}
        onCancel={() => {
          setConfirmingSignOut(false);
          triggerRef.current?.focus();
        }}
        onConfirm={() => {
          setSigningOut(true);
          signOut();
        }}
      />
    </div>
  );
}

/**
 * React 19: `ref` is an ordinary prop, so no `forwardRef` wrapper.
 */
function MenuItem({
  icon: Glyph,
  label,
  onSelect,
  danger,
  ref,
}: {
  icon: (props: React.SVGProps<SVGSVGElement>) => React.ReactElement;
  label: string;
  onSelect: () => void;
  danger?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      onClick={onSelect}
      className={cn(
        "rounded-control flex w-full items-center gap-2.5 px-2.5 py-2 text-left text-[0.8125rem]",
        "transition-colors duration-150",
        danger
          ? "text-danger hover:bg-danger-soft"
          : "text-content-muted hover:text-content hover:bg-surface-sunken",
      )}
    >
      <Glyph aria-hidden="true" className="size-4 shrink-0" />
      {label}
    </button>
  );
}
